import { useEffect, useLayoutEffect, useRef } from 'react';
import type { JSX, ReactNode } from 'react';
import { useLoadingState } from '../lib/useLoadingState.js';
import { BROUILLON_ID } from '../lib/creation.js';
import { Button } from '../components/Button.js';
import { SkeletonListRows } from '../components/Skeleton.js';
import { StateBlock } from '../components/ScreenLayout.js';
import { FALLBACK_VIEW_ID, ViewBoundary } from './ViewBoundary.js';
import type { ViewId, ViewProps } from './contract.js';
import { useViewModule } from './useViewModule.js';
import type { ScreenState } from './useScreen.js';

/**
 * VIEW HOST : la zone 5 quand elle est pilotee par une vue.
 *
 * Il monte la vue active et rien d'autre. Concretement il tient quatre choses
 * qu'aucune vue ne doit avoir a refaire :
 *
 * 1. LE CHARGEMENT PARESSEUX du module de la vue (`ViewType.load`), avec un
 *    cache par id : rebasculer sur une vue deja vue est instantane. Il vit dans
 *    `useViewModule` depuis que la CARTE LIEE d'une fiche monte elle aussi une
 *    vue du socle hors de la zone 5 : un seul cache, un seul chemin.
 * 2. LES ETATS STANDARD, dans la meme priorite STRICTE que le gabarit :
 *    erreur > chargement > fiche introuvable > vide > contenu. Le squelette
 *    affiche est celui de la VUE (`ViewType.Skeleton`), temporise par
 *    `useLoadingState` : pas de flash sur une donnee rapide, pas de
 *    clignotement, bascule en erreur au-dela de 10 s. L'etat VIDE lit aussi le
 *    MODE ARCHIVES (chantier 2_0) : c'est ici, et nulle part ailleurs, que le
 *    "Aucune fiche archivee" peut sortir sur un ecran a portes.
 * 3. LA MEMOIRE DE DEFILEMENT, par `viewId` (et `viewId:openId` en fiche) :
 *    retour a la liste, on retrouve la ligne 300, pas le haut de page.
 * 4. LE FILET (`ViewBoundary`) : une vue qui plante ne fait pas tomber l'ecran,
 *    le core retombe sur `core.liste` avec un avertissement en console.
 *
 * La priorite des etats est DELEGUEE ici : un ecran qui monte un ViewHost ne
 * passe plus `loading` / `error` / `empty` a `ScreenLayout`.
 */

/* LE CACHE DES MODULES a demenage dans `useViewModule` : la carte liee d'une
   fiche monte les memes vues, hors de la zone 5, et deux caches auraient charge
   deux fois le meme module. */

export interface ViewHostProps<T> {
  /** L'etat d'ecran rendu par `useScreen`. */
  state: ScreenState<T>;
  /** Titre de l'etat erreur. Defaut "Echec du chargement". */
  errorTitle?: string;
  /** Libelle du bouton de relance. Defaut "Reessayer". */
  retryLabel?: string;
  /** Titre de l'etat vide. Defaut "Rien pour l'instant". */
  emptyTitle?: string;
  /** Phrase d'aide de l'etat vide : elle dit QUOI faire pour demarrer. */
  emptyHint?: string;
  /** Titre quand la fiche ouverte est introuvable. Defaut "Fiche introuvable". */
  notFoundTitle?: string;
  /** Phrase d'aide de la fiche introuvable. */
  notFoundHint?: string;
  /** Vue de repli si la vue active plante. Defaut `core.liste`. */
  fallbackViewId?: ViewId;
  /** Section sous la fiche, dans la meme zone de defilement. */
  recordFooter?: ReactNode;
  /**
   * LE CONTENU DE LA CARTE DES FICHIERS JOINTS, relaye a la vue de FICHE
   * seulement (`ViewProps.fichiers`) : c'est elle qui pose la carte a son rang.
   * `null` rendu par la fonction : la carte n'apparait pas.
   */
  fichiers?: ViewProps['fichiers'];
  /**
   * LA FAMILLE DU JEU de la porte, relayee a la vue de FICHE seulement
   * (`ViewProps.collection`) : c'est ce qui permet de ranger une photo de
   * reference avec la fiche. Absente : pas de geste « photo ».
   */
  collection?: string;
}

export function ViewHost<T>({
  state,
  errorTitle = 'Échec du chargement',
  retryLabel = 'Réessayer',
  emptyTitle = "Rien pour l'instant",
  emptyHint,
  notFoundTitle = 'Fiche introuvable',
  notFoundHint = "Cet élément n'existe plus, ou tu n'y as pas accès.",
  fallbackViewId = FALLBACK_VIEW_ID,
  recordFooter,
  fichiers,
  collection,
}: ViewHostProps<T>): JSX.Element {
  const { activeType, viewId, openId, status, scrollRef } = state;
  const level = activeType?.level ?? 'collection';

  /* ---- Chargement paresseux du module de la vue ---- */

  const {
    module: loadedModule,
    error: moduleFailure,
    loading: moduleLoading,
  } = useViewModule(activeType);
  const Component = loadedModule?.Component ?? null;
  /**
   * LA FICHE OUVERTE EST UN BROUILLON DE CREATION, ET ELLE N'ATTEND PERSONNE.
   *
   * Sa donnee est DEJA LA (le moteur la tient en memoire, `rows[0]` existe des
   * le premier rendu) : la faire attendre la collection ferait clignoter un
   * squelette par-dessus une fiche vide qu'on est en train de remplir, et sur
   * une porte encore vide, l'etat « Rien pour l'instant » recouvrirait la fiche
   * qu'on vient d'ouvrir pour la remplir precisement parce qu'il n'y a rien.
   *
   * LE MODULE DE LA VUE, LUI, S'ATTEND TOUJOURS : sans composant, il n'y a rien
   * a rendre, et c'est le squelette qui le dit.
   */
  const brouillonOuvert = openId === BROUILLON_ID;
  const loading = (status.loading && !brouillonOuvert) || moduleLoading;

  const { showLoading, timedOut } = useLoadingState(loading);

  /* ---- Memoire de defilement, par vue (et par fiche ouverte) ---- */

  const scrollKey = level === 'record' && openId != null ? `${viewId}:${openId}` : viewId;
  const memoryRef = useRef(new Map<string, number>());
  const lastTopRef = useRef(0);
  const currentKeyRef = useRef(scrollKey);

  useEffect(() => {
    const element = scrollRef.current;
    if (element == null) return;
    const onScroll = (): void => {
      lastTopRef.current = element.scrollTop;
      memoryRef.current.set(currentKeyRef.current, element.scrollTop);
    };
    element.addEventListener('scroll', onScroll, { passive: true });
    return () => element.removeEventListener('scroll', onScroll);
  }, [scrollRef]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (element == null) return;
    if (currentKeyRef.current !== scrollKey) {
      // On range la position de la vue qu'on quitte AVANT de restaurer celle
      // qu'on ouvre : sinon un contenu plus court ecrase la memoire a 0.
      memoryRef.current.set(currentKeyRef.current, lastTopRef.current);
      currentKeyRef.current = scrollKey;
    }
    const remembered = memoryRef.current.get(scrollKey) ?? 0;
    element.scrollTop = remembered;
    lastTopRef.current = remembered;
  }, [scrollKey, scrollRef, Component]);

  /* ---- Etats standard : erreur > chargement > introuvable > vide > contenu ---- */

  const retry = (
    <Button variant="secondary" size="sm" onClick={() => state.reload()}>
      {retryLabel}
    </Button>
  );

  const failure = status.error ?? moduleFailure;
  if (failure != null) {
    return (
      <div className="h-full">
        <StateBlock role="alert" title={errorTitle} hint={failure.message} action={retry} />
      </div>
    );
  }

  if (loading && timedOut) {
    return (
      <div className="h-full">
        <StateBlock
          role="alert"
          title="Le chargement dure anormalement longtemps"
          action={retry}
        />
      </div>
    );
  }

  if (loading && showLoading) {
    // Le squelette appartient a la VUE : sa forme, ses rem, zero saut au swap.
    // Il vient du module charge (`load()` rend `{ Component, Skeleton? }`), ou
    // du descripteur quand une vue le declare en dur. Tant que le module n'est
    // pas la (tout premier affichage de cette vue), on retombe sur le squelette
    // de liste du gabarit. `aria-busy` porte l'etat, le squelette est decoratif.
    const Skeleton = activeType?.Skeleton ?? loadedModule?.Skeleton;
    return (
      <div aria-busy>
        <div key="skeleton" aria-hidden className="taki-anim-fade">
          {Skeleton != null ? <Skeleton /> : <SkeletonListRows />}
        </div>
      </div>
    );
  }

  if (loading) {
    // Fenetre de delai avant squelette : on n'affiche rien plutot que de faire
    // clignoter l'ecran pour une donnee qui arrive en 80 ms.
    return <div aria-busy className="h-full" />;
  }

  if (!brouillonOuvert && level === 'record' && state.rows.length === 0) {
    // Distinct de l'etat vide : la collection peut etre pleine, c'est CETTE
    // fiche qui manque (supprimee, ou sans droit de lecture).
    return (
      <div className="h-full">
        <StateBlock title={state.openDeleted ? 'Fiche supprimée' : notFoundTitle}
          hint={state.openDeleted ? 'Cette fiche a été supprimée : elle ne se rouvre plus.' : notFoundHint} />
      </div>
    );
  }

  if (!brouillonOuvert && status.empty) {
    /*
     * LES ETATS VIDES DES DEUX MODES SE DECIDENT ICI, ET PAS DANS `ScreenLayout`.
     *
     * Le gabarit porte bien la meme bascule dans sa branche `empty`, mais
     * AUCUN ecran a portes ne lui passe `empty` : tous delegent la zone 5 au
     * ViewHost, qui tient la priorite des etats. Le texte du mode etait donc du
     * code mort, et ce qu'on lisait a l'ecran restait le message de l'app.
     *
     * Ce message-la est faux deux fois en mode archives. Sur la porte
     * "Brouillons" du cockpit, il dit "Aucun projet d'app pour l'instant /
     * Clique sur Ajouter au-dessus de la liste" au moment PRECIS ou le chassis
     * vient de DESACTIVER "+ Ajouter" : l'ecran se contredit en deux lignes, et
     * il annonce un vide de la collection alors que seules les ARCHIVES sont
     * vides. Ici, il n'y a rien D'ARCHIVE, et la sortie est nommee dans la
     * phrase.
     */
    return (
      <div className="h-full">
        {state.archivesMode ? (
          <StateBlock
            title="Aucune fiche archivée"
            hint="« Quitter Archives » ramène au travail courant."
          />
        ) : state.modelesMode ? (
          /*
           * ET LE MODE MODELES A LE SIEN, POUR LA MEME RAISON EXACTEMENT. Le
           * message de l'app est faux deux fois ici aussi : il annonce un vide
           * de la COLLECTION alors que seule la liste des MODELES est vide, et
           * il renvoie vers un « + Ajouter » que le mode a remplace par « Quitter Modeles ».
           *
           * L'INDICE NOMME LES DEUX CHEMINS, parce qu'il y en a deux et qu'ils
           * ne se ressemblent pas : creer un modele nu ici, ou partir d'une
           * fiche existante et l'enregistrer comme modele depuis son menu. Une
           * liste de modeles vide est l'etat de DEPART de toute porte qui
           * allume les modeles ; c'est le seul ecran qui puisse dire comment en
           * sortir.
           */
          <StateBlock
            title="Aucun modèle pour cette porte"
            hint="Crée un modèle depuis le menu …, ou enregistre une fiche comme modèle."
          />
        ) : (
          <StateBlock title={emptyTitle} {...(emptyHint != null ? { hint: emptyHint } : {})} />
        )}
      </div>
    );
  }

  if (activeType == null || Component == null) {
    // Aucune vue montable (catalogue vide) : rien a rendre, pas d'erreur.
    return <div className="h-full" />;
  }

  /* ---- La vue ---- */

  // Ce que la vue recoit est assemble par `useScreen`, a UN seul endroit : le
  // meme objet sert a calculer les actions de vue de la zone 6
  // (`ViewType.actions`), qui sont rendues par le rail, hors d'ici.
  //
  // LE HOST NE TRIE RIEN, ET C'EST CE QUI LE REND SUR. Il etale `viewProps` tel
  // quel : ajouter une entree dans `useScreen` suffit a la faire arriver a la
  // vue, sans un seul recablage ici. C'est comme ca que la SELECTION (chantier
  // 3_0) voyage, et c'est la seule raison pour laquelle ce fichier n'a pas
  // change : `useScreen` ne pose `selection` / `onSelectionChange` QUE quand la
  // vue active est le tableau, donc la liste et le kanban ne recoivent rien a
  // ignorer. Ne "filtre" jamais ces props ici : le maillon de transport qui
  // decide a la place du core est exactement celui que personne ne relit, et
  // une prop qu'il oublie de passer ne fait rougir aucun build.
  const viewProps = state.viewProps;

  const selfScroll = activeType.scroll === 'self';

  /*
   * Le bandeau dit ce qui MANQUE, et les deux modes de chargement ne cachent
   * pas la meme chose. En mode client tout est charge, on en affiche une partie :
   * affiner la recherche suffit a retrouver une ligne. En mode serveur le reste
   * n'est pas la du tout, et surtout la recherche et les pastilles ne portent
   * QUE sur les pages chargees : une ligne absente peut tres bien exister. Le
   * taire ferait conclure a l'utilisateur qu'un element n'existe pas.
   */
  const banner = status.truncated ? (
    <div className="flex items-center justify-between gap-4 border-b border-border-soft px-5 py-2">
      <p className="min-w-0 text-xs text-ink-500">
        {state.pagination === 'server'
          ? `${state.records.length} lignes chargées. La recherche et les filtres ne portent que sur elles.`
          : `Affinez la recherche : seules les ${state.query.limit} premières lignes sont affichées.`}
      </p>
      <Button variant="secondary" size="sm" onClick={() => state.loadMore()}>
        {state.pagination === 'server' ? 'Charger la suite' : 'Afficher plus'}
      </Button>
    </div>
  ) : null;

  /*
   * LA VUE DE REPLI NE PEUT PAS ETRE CELLE QUI PLANTE.
   *
   * `ViewBoundary` refuse de boucler, et il a raison : quand la vue fautive EST
   * la vue de repli, il ne demande AUCUNE bascule et peint son bloc d'alerte a
   * la place. Mais le core, lui, ne voit rien passer : `status.error` reste nul,
   * `rows` est intact, et la vue active reste celle qu'on croyait montee. La
   * BARRE DE LOT (chantier 3_0) continue alors d'annoncer "3 elements
   * selectionnes" avec ses trois boutons VIVANTS au-dessus d'un ecran qui ne
   * montre plus une seule ligne : archiver ou supprimer ecrirait sur des fiches
   * que personne ne peut plus voir ni verifier. Le moteur ferme deja ce trou
   * pour l'ERREUR DE CHARGEMENT (`useScreen`, `visibleSelection`) ; celui-ci
   * n'existe que par un `fallbackViewId` que seul un hote peut poser, et c'est
   * ici, au seul endroit qui connait les deux ids, qu'il se ferme.
   *
   * Des que les deux coincident, on rend au boundary la vue UNIVERSELLE : il
   * bascule, `useScreen` change de vue, la selection tombe avec sa surface, et
   * l'utilisateur garde un ecran qui marche au lieu d'un bloc d'alerte. Et
   * quand la vue universelle est elle-meme la fautive, ce calcul la rend telle
   * quelle : le boundary retrouve ses deux ids egaux et s'arrete sur son
   * message sobre, sans boucler.
   */
  const safeFallbackViewId = fallbackViewId === viewId ? FALLBACK_VIEW_ID : fallbackViewId;

  const body: ReactNode = (
    <ViewBoundary
      viewId={viewId}
      fallbackViewId={safeFallbackViewId}
      onFallback={state.setView}
      renderError={(message) => (
        <div className="h-full">
          <StateBlock role="alert" title={errorTitle} hint={message} action={retry} />
        </div>
      )}
    >
      {/* LA SEULE PROP QUE LE HOST AJOUTE, et seulement au niveau FICHE : elle
          ne vient pas du moteur (`useScreen` ne sait rien des fichiers) mais
          de l'hote, comme `recordFooter`. Les autres vues n'ont pas de carte. */}
      <Component
        {...viewProps}
        {...(level === 'record' && fichiers !== undefined ? { fichiers } : {})}
        {...(level === 'record' && collection !== undefined ? { collection } : {})}
      />
    </ViewBoundary>
  );

  // `contain: layout` isole la vue : elle ne peut pas deformer le gabarit, et
  // tout positionnement absolu reste dans sa boite (garde-fou anti-portail).
  if (selfScroll) {
    // La vue possede sa barre de defilement : sa boite est a hauteur fixe.
    return (
      <div className="flex h-full min-h-0 flex-col" style={{ contain: 'layout' }}>
        {banner}
        <div className="min-h-0 flex-1 overflow-hidden">{body}</div>
      </div>
    );
  }

  // La zone 5 possede la barre de defilement : la boite grandit avec le contenu.
  return (
    <div className="min-h-full" style={{ contain: 'layout' }}>
      {banner}
      {body}
      {level === 'record' ? recordFooter : null}
      {/* LE LANCEUR DE LIA (bas droit) NE MASQUE RIEN : le shell publie sa hauteur
          (`--taki-launcher-clearance`), et la derniere ligne defile au-dessus. */}
      <div aria-hidden="true" className="h-[var(--taki-launcher-clearance,0px)]" />
    </div>
  );
}
